import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { keys } from "@/lib/api/query-keys";
import { MESSAGE_PAGE_SIZE } from "../api/chat.queries";

/**
 * The bridge's rules, apart from the component that applies them — so they can be tested (and
 * fast-refreshed) without rendering anything.
 */

/** How long frames are gathered before the queries are asked. */
export const COALESCE_MS = 250;

/** The rail, not the detail rows that share its prefix. */
export function isConversationListKey(key: QueryKey): boolean {
  return key[0] === "chat" && key[1] === "conversations" && key[2] !== "detail";
}

export type Invalidation =
  | { kind: "list" }
  | { kind: "unread" }
  /** The bell's count and open list — my own read settled chat rows (H4, 20 Ekim 2026). */
  | { kind: "bell" }
  | { kind: "detail"; conversationId: string }
  | { kind: "thread"; conversationId: string };

export function keyOf(invalidation: Invalidation): string {
  return invalidation.kind === "list" || invalidation.kind === "unread" || invalidation.kind === "bell"
    ? invalidation.kind
    : `${invalidation.kind}:${invalidation.conversationId}`;
}

export function applyInvalidation(queryClient: QueryClient, invalidation: Invalidation): void {
  switch (invalidation.kind) {
    case "list":
      void queryClient.invalidateQueries({ predicate: (query) => isConversationListKey(query.queryKey) });
      return;
    case "unread":
      void queryClient.invalidateQueries({ queryKey: keys.chat.unreadCount() });
      return;
    case "bell":
      void queryClient.invalidateQueries({ queryKey: keys.notifications.unreadCount() });
      void queryClient.invalidateQueries({ queryKey: ["notifications", "list"] });
      return;
    case "detail":
      void queryClient.invalidateQueries({ queryKey: keys.chat.conversation(invalidation.conversationId) });
      return;
    case "thread":
      void queryClient.invalidateQueries({
        queryKey: keys.chat.messages(invalidation.conversationId, { limit: MESSAGE_PAGE_SIZE }),
      });
  }
}

